import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { CheckRadioForLists } from '../../../_lib/Form2';
import { AdditionalPrivilegeCheckBoxParam } from './AdditionalPrivilegeConfigEditForm';
import { PrivilegeCheckboxTree } from './PrivilegeCheckboxTree';
import { Icons, SVG } from '../../../components/atoms/SVG';
import { RequiredPrivilegeHints } from '../partials/RequiredPrivilegeHints';

type EnrichedAdditionalPrivilegeCheckBoxParam = {
  label: string;
} & AdditionalPrivilegeCheckBoxParam;

type PrivilegeCheckboxTreeNodeProps = {
  privilegeFormParam: EnrichedAdditionalPrivilegeCheckBoxParam;
  onPrivilegeChecked?: () => void;
};

export function PrivilegeCheckboxTreeNode({ privilegeFormParam, onPrivilegeChecked }: PrivilegeCheckboxTreeNodeProps) {
  const { t } = useSonTranslation('PrivilegeConfig');
  // DOM reference to the <input type=checkbox> of the currently rendered privilege:
  const ownCheckboxRef = useRef<HTMLInputElement>(null);
  // DOM reference to the <ul> that contains the tree of dependent (child) privileges (if any):
  const dependentPrivilegesTreeRef = useRef<HTMLUListElement>(null);
  const [isChecked, setIsChecked] = useState(false);

  const markCurrentAndParentPrivilegesAsChecked = () => {
    if (ownCheckboxRef.current) {
      ownCheckboxRef.current.checked = true;
      // we need setIsChecked here because this function can be called either via
      // handleOwnCheckboxChange() or onAnyChildPrivilegeChecked()
      setIsChecked(true);
      onPrivilegeChecked?.(); // bubble up the privileges tree
    }
  };

  const uncheckAllDependentPrivileges = () => {
    // dependentPrivilegesTreeRef is a reference to the <ul> that contains the checkbox inputs
    // of all dependent (child) privileges, so we can simply uncheck all checkboxes within that DOM node.
    const allDependentPrivilegeCheckboxes =
      dependentPrivilegesTreeRef.current?.querySelectorAll('input[type="checkbox"]');

    allDependentPrivilegeCheckboxes?.forEach(dependentPrivilege => {
      (dependentPrivilege as HTMLInputElement).checked = false;
    });
  };

  const handleOwnCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      markCurrentAndParentPrivilegesAsChecked();
      setIsChecked(true);
    } else {
      uncheckAllDependentPrivileges();
      setIsChecked(false);
    }
  };

  // show related icon where needed.
  // there is no dependency array because we want to check that on every update.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (ownCheckboxRef.current) {
      if (ownCheckboxRef.current.checked) {
        setIsChecked(true);
      } else {
        setIsChecked(false);
      }
    }
  });

  return (
    <>
      <div className="privilege-config__checkbox-container">
        {isChecked && (
          <div className="privilege-config__related-icon">
            <SVG svgSpriteId={Icons.PAGE_TO_PAGE_ROLE_RELATED} />
          </div>
        )}
        {/* render a checkbox for the current privilege */}
        <CheckRadioForLists
          type="checkbox"
          param={privilegeFormParam}
          label={privilegeFormParam.label}
          ref={ownCheckboxRef}
          onChange={handleOwnCheckboxChange}
          description={t(`input.${privilegeFormParam.value?.toLocaleLowerCase()}.description` as any)}
        >
          {privilegeFormParam.requiredBasicPrivileges && privilegeFormParam.requiredBasicPrivileges.length > 0 ? (
            <RequiredPrivilegeHints
              requiredBasicPrivileges={privilegeFormParam.requiredBasicPrivileges}
              parentAdditionalPrivilegeLabels={[]}
              missingPrivilegeIconId={Icons.INFO_MESSAGE}
              missingPrivilegeHelpText={t('requiredPrivilegeGrantedAutomatically')}
            />
          ) : null}
        </CheckRadioForLists>
      </div>
      {/* render a subtree of checkboxes for all dependent privileges */}
      {privilegeFormParam.dependentPrivileges?.length > 0 ? (
        <div className="privilege-config__additional-privilege-sub-tree">
          <PrivilegeCheckboxTree
            privilegeParams={privilegeFormParam.dependentPrivileges}
            onAnyChildPrivilegeChecked={markCurrentAndParentPrivilegesAsChecked}
            ref={dependentPrivilegesTreeRef}
          />
        </div>
      ) : null}
    </>
  );
}
